/**
 * collection-filter.js — ChipSync UAE IT Distributor
 * Installation: themes/{theme}/assets/collection-filter.js
 *
 * Features:
 *  - AJAX filter reload (no page refresh)
 *  - URL param state (back/forward works)
 *  - Price range dual-handle slider
 *  - Filter accordion toggle
 *  - Brand search-within
 *  - Mobile drawer (bottom sheet)
 *  - Active filter chips
 *  - Quick View modal
 *  - Compare (up to 4 products)
 *  - Load More pagination
 *  - Skeleton loader
 */

(function () {
  'use strict';

  /* ─────────────────────────────────────────────────────────
     STATE
  ───────────────────────────────────────────────────────── */
  var cfState = {
    compareIds:     [],
    compareItems:   [],
    activeFilters:  {},
    isLoading:      false,
    priceMin:       0,
    priceMax:       30000
  };

  /* ─────────────────────────────────────────────────────────
     INIT
  ───────────────────────────────────────────────────────── */
  document.addEventListener('DOMContentLoaded', function () {
    cfPriceSliderInit();
    cfFormListeners();
    cfReadUrlParams();
    cfRenderChips();
    cfUpdateCount();
    cfUpdateBadge();
    cfInitPaginationLinks();
    window.addEventListener('popstate', cfOnPopState);
  });

  /* ─────────────────────────────────────────────────────────
     PAGINATION — intercept clicks and carry filter params
  ───────────────────────────────────────────────────────── */
  function cfInitPaginationLinks () {
    var pag = document.getElementById('cf-pagination');
    if (!pag) return;

    pag.querySelectorAll('a').forEach(function (link) {
      // Avoid double-binding
      if (link.dataset.cfBound) return;
      link.dataset.cfBound = '1';

      link.addEventListener('click', function (e) {
        e.preventDefault();

        var href   = link.getAttribute('href');        // e.g. /collections/all?page=2
        var url    = new URL(href, window.location.origin);
        var params = cfBuildParams();                  // current filter state

        // Merge filter params into the pagination URL
        params.forEach(function (value, key) {
          url.searchParams.set(key, value);
        });

        // Navigate + fetch
        history.pushState({}, '', url.toString());
        cfFetchProducts(new URLSearchParams(url.search));

        // Scroll back to top of product grid
        var grid = document.getElementById('cf-main');
        if (grid) grid.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  }

  /* ─────────────────────────────────────────────────────────
     ACCORDION TOGGLE
  ───────────────────────────────────────────────────────── */
  window.cfToggleGroup = function (btn) {
    var group = btn.closest('.cf-filter-group');
    var isOpen = group.classList.contains('is-open');
    group.classList.toggle('is-open', !isOpen);
    btn.setAttribute('aria-expanded', String(!isOpen));
  };

  /* ─────────────────────────────────────────────────────────
     SHOW MORE
  ───────────────────────────────────────────────────────── */
  window.cfShowMore = function (btn) {
    var listId = btn.dataset.listId;
    var list   = document.getElementById(listId);
    if (!list) return;
    var hidden = list.querySelectorAll('.cf-opt-hidden');
    hidden.forEach(function (el) { el.classList.remove('cf-opt-hidden'); });
    btn.style.display = 'none';
  };

  /* ─────────────────────────────────────────────────────────
     SEARCH WITHIN FILTER
  ───────────────────────────────────────────────────────── */
  window.cfSearchOptions = function (input, groupId) {
    var q    = input.value.trim().toLowerCase();
    var list = document.getElementById('fg-list-' + groupId);
    if (!list) return;
    var items = list.querySelectorAll('.cf-filter-group__item');
    items.forEach(function (item) {
      var label = (item.dataset.optLabel || '');
      item.style.display = (!q || label.indexOf(q) !== -1) ? '' : 'none';
    });
  };

  function cfStemWord (w) {
    if (w.length <= 3) return w;
    return w.replace(/ies$/, 'y').replace(/ves$/, 'f').replace(/es$/, '').replace(/s$/, '');
  }

  window.cfSearchGroup = function (input, groupId) {
    var raw   = input.value.trim().toLowerCase();
    var group = document.getElementById(groupId);
    if (!group) return;
    var items = group.querySelectorAll('.cf-filter-group__item');
    if (!raw) {
      items.forEach(function (item) { item.style.display = ''; });
      return;
    }
    var qWords = raw.split(/\s+/).map(cfStemWord);
    items.forEach(function (item) {
      var label  = item.textContent.trim().toLowerCase();
      var lWords = label.split(/\s+/).map(cfStemWord);
      // every query word must loosely match at least one label word
      var match = qWords.every(function (qw) {
        return lWords.some(function (lw) {
          return lw.indexOf(qw) !== -1 || qw.indexOf(lw) !== -1;
        });
      });
      item.style.display = match ? '' : 'none';
    });
  };

  /* ─────────────────────────────────────────────────────────
     FORM LISTENERS — respond to any checkbox change
  ───────────────────────────────────────────────────────── */
  function cfFormListeners () {
    var form = document.getElementById('cf-filter-form');
    if (!form) return;
    form.addEventListener('change', function (e) {
      var el = e.target;
      if (el.classList.contains('cf-checkbox__input') || el.classList.contains('cf-toggle-input')) {
        cfOnFilterChange();
      }
    });
  }

  function cfOnFilterChange () {
    var params = cfBuildParams();
    // Always go back to page 1 when a filter changes
    params.delete('page');
    cfPushState(params);
    cfFetchProducts(params);
    cfRenderChips();
    cfUpdateCount();
    cfUpdateBadge();
    cfSyncGroupCounts();
  }

  /* ─────────────────────────────────────────────────────────
     BUILD URL PARAMS FROM FORM
  ───────────────────────────────────────────────────────── */
  function cfBuildParams () {
    var form   = document.getElementById('cf-filter-form');
    var params = new URLSearchParams();

    if (form) {
      var inputs = form.querySelectorAll('input[type="checkbox"]:checked');
      inputs.forEach(function (cb) {
        params.append(cb.name, cb.value);
      });
    }

    // Availability toggle
    var availToggle = document.getElementById('cf-avail-toggle');
    if (availToggle && availToggle.checked) {
      params.append(availToggle.name, availToggle.value);
    }

    // Price
    var minEl = document.getElementById('cf-price-min');
    var maxEl = document.getElementById('cf-price-max');
    if (minEl && parseInt(minEl.value, 10) > 0) {
      params.set('filter.v.price.gte', minEl.value);
    }
    if (maxEl && parseInt(maxEl.value, 10) < 30000) {
      params.set('filter.v.price.lte', maxEl.value);
    }

    return params;
  }

  /* ─────────────────────────────────────────────────────────
     AJAX PRODUCT FETCH
  ───────────────────────────────────────────────────────── */
  function cfFetchProducts (params) {
    if (cfState.isLoading) return;
    cfState.isLoading = true;
    cfShowSkeleton();

    var base = window.location.pathname;
    var queryStr = params.toString();
    var url  = base + (queryStr ? '?' + queryStr : '') + (queryStr ? '&' : '?') + 'sections=collection-filter';

    fetch(url, {
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    })
    .then(function (res) {
      if (!res.ok) throw new Error('Network error ' + res.status);
      return res.json();
    })
    .then(function (data) {
      var html = data['collection-filter'];
      if (html) {
        var parser  = new DOMParser();
        var doc     = parser.parseFromString(html, 'text/html');

        // ── Update product grid ──
        var newGrid = doc.querySelector('[data-product-grid]');
        var oldGrid = document.querySelector('[data-product-grid]');
        if (newGrid && oldGrid) {
          oldGrid.innerHTML = newGrid.innerHTML;
        }

        // ── Update result count ──
        var newCount = doc.getElementById('cf-visible-count');
        var oldCount = document.getElementById('cf-visible-count');
        if (newCount && oldCount) {
          oldCount.textContent = newCount.textContent;
        }

        // ── Update pagination ──
        var newPag = doc.getElementById('cf-pagination');
        var oldPag = document.getElementById('cf-pagination');
        if (oldPag) {
          oldPag.innerHTML = newPag ? newPag.innerHTML : '';
        }

        // ── Re-attach pagination link click handlers ──
        cfInitPaginationLinks();
      }
    })
    .catch(function (err) {
      console.warn('[ChipSync Filter] Fetch error:', err);
      var grid = document.querySelector('[data-product-grid]');
      if (grid) grid.innerHTML = '<div style="padding:3rem;text-align:center;color:var(--text-secondary);grid-column:1/-1">Failed to load products. Please refresh and try again.</div>';
    })
    .finally(function () {
      cfState.isLoading = false;
      cfHideSkeleton();
    });
  }

  function cfShowSkeleton () {
    var sk = document.getElementById('cf-skeleton');
    if (sk) sk.style.display = 'contents';
  }
  function cfHideSkeleton () {
    var sk = document.getElementById('cf-skeleton');
    if (sk) sk.style.display = 'none';
  }

  /* ─────────────────────────────────────────────────────────
     URL STATE
  ───────────────────────────────────────────────────────── */
  function cfPushState (params) {
    var url = window.location.pathname + (params.toString() ? '?' + params.toString() : '');
    history.pushState({ filters: params.toString() }, '', url);
  }

  function cfReadUrlParams () {
    var params = new URLSearchParams(window.location.search);
    if (!params.toString()) return;
    var form = document.getElementById('cf-filter-form');
    if (!form) return;
    params.forEach(function (value, key) {
      var cb = form.querySelector('input[name="' + key + '"][value="' + value + '"]');
      if (cb) {
        cb.checked = true;
        var lbl = cb.closest('.cf-checkbox, .cf-brand-pill');
        if (lbl) lbl.classList.add('is-checked');
      }
    });
    // Price
    var pMin = params.get('filter.v.price.gte');
    var pMax = params.get('filter.v.price.lte');
    if (pMin) {
      var el = document.getElementById('cf-price-min');
      if (el) { el.value = pMin; cfPriceSync(); }
    }
    if (pMax) {
      var el2 = document.getElementById('cf-price-max');
      if (el2) { el2.value = pMax; cfPriceSync(); }
    }
  }

  function cfOnPopState (e) {
    var form = document.getElementById('cf-filter-form');
    if (form) {
      form.querySelectorAll('input[type="checkbox"]').forEach(function (cb) {
        cb.checked = false;
        var lbl = cb.closest('.cf-checkbox, .cf-brand-pill');
        if (lbl) lbl.classList.remove('is-checked');
      });
    }
    var availToggle = document.getElementById('cf-avail-toggle');
    if (availToggle) availToggle.checked = false;
    cfReadUrlParams();
    var params = cfBuildParams();
    cfFetchProducts(params);
    cfRenderChips();
    cfUpdateCount();
  }

  /* ─────────────────────────────────────────────────────────
     CLEAR ALL FILTERS
  ───────────────────────────────────────────────────────── */
  window.cfClearAll = function () {
    var form = document.getElementById('cf-filter-form');
    if (form) {
      form.querySelectorAll('input[type="checkbox"]').forEach(function (cb) {
        cb.checked = false;
        var lbl = cb.closest('.cf-checkbox, .cf-brand-pill');
        if (lbl) lbl.classList.remove('is-checked');
      });
    }
    var availToggle = document.getElementById('cf-avail-toggle');
    if (availToggle) availToggle.checked = false;
    // Reset price
    var minEl = document.getElementById('cf-price-min');
    var maxEl = document.getElementById('cf-price-max');
    if (minEl) minEl.value = 0;
    if (maxEl) maxEl.value = 30000;
    cfPriceSync();

    cfRenderChips();
    cfUpdateCount();
    cfUpdateBadge();
    cfSyncGroupCounts();

    var params = new URLSearchParams();
    cfPushState(params);
    cfFetchProducts(params);
    cfCloseDrawer();
  };

  /* ─────────────────────────────────────────────────────────
     ACTIVE FILTER CHIPS
  ───────────────────────────────────────────────────────── */
  function cfRenderChips () {
    var form   = document.getElementById('cf-filter-form');
    var bars   = document.querySelectorAll('.cf-chips-bar');
    var chips  = '';

    if (form) {
      form.querySelectorAll('input[type="checkbox"]:checked').forEach(function (cb) {
        chips += cfMakeChip(cb.value, cb.name, cb.value);
      });
    }

    // Price chip
    var minEl = document.getElementById('cf-price-min');
    var maxEl = document.getElementById('cf-price-max');
    var pMin  = minEl ? parseInt(minEl.value, 10) : 0;
    var pMax  = maxEl ? parseInt(maxEl.value, 10) : 30000;
    if (pMin > 0 || pMax < 30000) {
      chips += cfMakeChip(
        'AED ' + cfFmtNum(pMin) + ' – AED ' + cfFmtNum(pMax),
        'price', 'price'
      );
    }

    bars.forEach(function (bar) { bar.innerHTML = chips; });

    // Show/hide clear all button
    var clearBtn = document.getElementById('cf-clear-all');
    if (clearBtn) {
      clearBtn.style.display = chips ? 'inline-flex' : 'none';
    }
  }

  function cfMakeChip (label, name, value) {
    return '<button class="cf-chip" type="button" '
      + 'onclick="cfRemoveChip(\'' + cfEsc(name) + '\',\'' + cfEsc(value) + '\')" '
      + 'aria-label="Remove filter: ' + cfEsc(label) + '">'
      + cfEsc(label)
      + '<span class="cf-chip__remove" aria-hidden="true">×</span>'
      + '</button>';
  }

  window.cfRemoveChip = function (name, value) {
    if (name === 'price') {
      var minEl = document.getElementById('cf-price-min');
      var maxEl = document.getElementById('cf-price-max');
      if (minEl) minEl.value = 0;
      if (maxEl) maxEl.value = 30000;
      cfPriceSync();
    } else {
      var form = document.getElementById('cf-filter-form');
      if (form) {
        var cb = form.querySelector('input[name="' + name + '"][value="' + value + '"]');
        if (cb) {
          cb.checked = false;
          var lbl = cb.closest('.cf-checkbox, .cf-brand-pill');
          if (lbl) lbl.classList.remove('is-checked');
        }
      }
    }
    cfOnFilterChange();
  };

  function cfUpdateCount () {
    var form  = document.getElementById('cf-filter-form');
    var badge = document.getElementById('cf-active-count-badge');
    if (!badge) return;
    var count = 0;
    if (form) {
      count = form.querySelectorAll('input[type="checkbox"]:checked').length;
    }
    var minEl = document.getElementById('cf-price-min');
    var maxEl = document.getElementById('cf-price-max');
    if (minEl && parseInt(minEl.value, 10) > 0) count++;
    if (maxEl && parseInt(maxEl.value, 10) < 30000) count++;
    badge.textContent = count;
    badge.style.display = count > 0 ? 'inline-flex' : 'none';
  }

  function cfSyncGroupCounts () {
    document.querySelectorAll('.cf-filter-group').forEach(function (group) {
      var checked = group.querySelectorAll('input[type="checkbox"]:checked').length;
      var countEl = group.querySelector('.cf-filter-group__count');
      if (countEl) {
        countEl.textContent = checked;
        countEl.style.display = checked > 0 ? 'inline-flex' : 'none';
      }
    });
  }

  /* ─────────────────────────────────────────────────────────
     MOBILE FILTER BADGE
  ───────────────────────────────────────────────────────── */
  function cfUpdateBadge () {
    var form  = document.getElementById('cf-filter-form');
    var badge = document.getElementById('cf-filter-badge');
    if (!badge) return;
    var count = 0;
    if (form) {
      count = form.querySelectorAll('input[type="checkbox"]:checked').length;
    }
    var minEl = document.getElementById('cf-price-min');
    var maxEl = document.getElementById('cf-price-max');
    if (minEl && parseInt(minEl.value, 10) > 0) count++;
    if (maxEl && parseInt(maxEl.value, 10) < 30000) count++;
    badge.textContent  = count > 0 ? count : '';
    badge.style.display = count > 0 ? 'inline-flex' : 'none';
  }
  window.cfUpdateBadge = cfUpdateBadge;

  /* ─────────────────────────────────────────────────────────
     SORT
  ───────────────────────────────────────────────────────── */
  window.cfSortChange = function (value) {
    if (!value) return;
    var params = cfBuildParams();
    params.set('sort_by', value);
    cfPushState(params);
    window.location.href = window.location.pathname + '?' + params.toString();
  };

  /* ─────────────────────────────────────────────────────────
     PRICE SLIDER
  ───────────────────────────────────────────────────────── */
  function cfPriceSliderInit () {
    var minSlider  = document.getElementById('cf-price-min');
    var maxSlider  = document.getElementById('cf-price-max');
    var minText    = document.getElementById('cf-price-min-text');
    var maxText    = document.getElementById('cf-price-max-text');

    if (!minSlider || !maxSlider) return;

    minSlider.addEventListener('input', function () {
      if (parseInt(minSlider.value, 10) >= parseInt(maxSlider.value, 10)) {
        minSlider.value = parseInt(maxSlider.value, 10) - 50;
      }
      if (minText) minText.value = minSlider.value;
      cfPriceSync();
    });

    maxSlider.addEventListener('input', function () {
      if (parseInt(maxSlider.value, 10) <= parseInt(minSlider.value, 10)) {
        maxSlider.value = parseInt(minSlider.value, 10) + 50;
      }
      if (maxText) maxText.value = maxSlider.value;
      cfPriceSync();
    });

    // Text inputs drive sliders
    if (minText) {
      minText.addEventListener('change', function () {
        var val = Math.max(0, Math.min(parseInt(minText.value, 10) || 0, 29950));
        minSlider.value = val;
        cfPriceSync();
      });
    }
    if (maxText) {
      maxText.addEventListener('change', function () {
        var val = Math.min(30000, Math.max(parseInt(maxText.value, 10) || 30000, 50));
        maxSlider.value = val;
        cfPriceSync();
      });
    }

    // Trigger filter on mouseup / touchend
    [minSlider, maxSlider].forEach(function (s) {
      s.addEventListener('change', function () {
        cfOnFilterChange();
      });
    });

    cfPriceSync();
  }

  function cfPriceSync () {
    var minSlider = document.getElementById('cf-price-min');
    var maxSlider = document.getElementById('cf-price-max');
    var fill      = document.getElementById('cf-price-fill');
    var label     = document.getElementById('cf-price-label');
    if (!minSlider || !maxSlider) return;

    var min  = parseInt(minSlider.value, 10);
    var max  = parseInt(maxSlider.value, 10);
    var pct1 = (min / 30000) * 100;
    var pct2 = (max / 30000) * 100;

    if (fill) {
      fill.style.left  = pct1 + '%';
      fill.style.width = (pct2 - pct1) + '%';
    }
    if (label) {
      var maxLabel = max >= 30000 ? 'AED 30,000+' : 'AED ' + cfFmtNum(max);
      label.textContent = 'AED ' + cfFmtNum(min) + ' — ' + maxLabel;
    }
    // Sync text inputs
    var minText = document.getElementById('cf-price-min-text');
    var maxText = document.getElementById('cf-price-max-text');
    if (minText) minText.value = min;
    if (maxText) maxText.value = max;
  }

  /* ─────────────────────────────────────────────────────────
     MOBILE DRAWER
  ───────────────────────────────────────────────────────── */
  window.cfOpenDrawer = function () {
    var overlay = document.getElementById('cf-drawer-overlay');
    var drawer  = document.getElementById('cf-drawer');
    var body    = document.getElementById('cf-drawer-body');
    var sidebar = document.getElementById('cf-sidebar');

    if (!drawer || !sidebar) return;

    // Clone the filter form into the drawer
    if (body) {
      var sidebarForm = sidebar.querySelector('#cf-filter-form');
      if (!sidebarForm) return;
      var clone = sidebarForm.cloneNode(true);
      clone.id  = 'cf-drawer-form';
      body.innerHTML = '';
      body.appendChild(clone);

      // Sync checked state
      clone.querySelectorAll('input[type="checkbox"]').forEach(function (cb) {
        var orig = document.getElementById('cf-filter-form')
          .querySelector('input[name="' + cb.name + '"][value="' + cb.value + '"]');
        if (orig && orig.checked) {
          cb.checked = true;
          var lbl = cb.closest('.cf-checkbox, .cf-brand-pill');
          if (lbl) lbl.classList.add('is-checked');
        }
      });

      // Drawer checkboxes update sidebar checkboxes
      clone.addEventListener('change', function (e) {
        var drawerCb = e.target;
        var origCb   = document.getElementById('cf-filter-form')
          .querySelector('input[name="' + drawerCb.name + '"][value="' + drawerCb.value + '"]');
        if (origCb) {
          origCb.checked = drawerCb.checked;
          var lbl = origCb.closest('.cf-checkbox, .cf-brand-pill');
          if (lbl) lbl.classList.toggle('is-checked', drawerCb.checked);
        }
        var dl = drawerCb.closest('.cf-checkbox, .cf-brand-pill');
        if (dl) dl.classList.toggle('is-checked', drawerCb.checked);
      });
    }

    overlay && overlay.classList.add('is-open');
    drawer.classList.add('is-open');
    document.body.style.overflow = 'hidden';
  };

  window.cfCloseDrawer = function () {
    var overlay = document.getElementById('cf-drawer-overlay');
    var drawer  = document.getElementById('cf-drawer');
    overlay && overlay.classList.remove('is-open');
    drawer && drawer.classList.remove('is-open');
    document.body.style.overflow = '';
  };

  window.cfApplyDrawer = function () {
    cfOnFilterChange();
    cfCloseDrawer();
  };

  /* ─────────────────────────────────────────────────────────
     QUICK VIEW MODAL
  ───────────────────────────────────────────────────────── */
  window.cfOpenQuickView = function (btn) {
    var overlay  = document.getElementById('cf-qv-overlay');
    var modal    = document.getElementById('cf-qv-modal');
    var content  = document.getElementById('cf-qv-content');
    var prodUrl  = btn.dataset.productUrl;

    if (!modal) return;

    // Show loading state
    content.innerHTML = '<div class="cf-qv-loading">'
      + '<div class="cf-spinner"></div><p>Loading…</p></div>';

    overlay && overlay.classList.add('is-open');
    modal.classList.add('is-open');
    document.body.style.overflow = 'hidden';

    if (!prodUrl) return;

    fetch(prodUrl + '?sections=product-quick-view', {
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    })
    .then(function (res) { return res.text(); })
    .then(function (html) {
      // Build a simple quick view from product page HTML
      var parser   = new DOMParser();
      var doc      = parser.parseFromString(html, 'text/html');
      var title    = doc.querySelector('h1, .pd-title');
      var price    = doc.querySelector('.pd-price, .prd-card-price');
      var img      = doc.querySelector('.pd-main-img img, .prd-card-img img');
      var vendor   = doc.querySelector('.pd-vendor, .prd-card-vendor');
      var specs    = doc.querySelector('.pd-description, .prd-card-desc');
      var sku      = doc.querySelector('.pd-sku, .cf-card-pn');

      content.innerHTML = '<div class="cf-qv-product">'
        + '<div class="cf-qv-img">'
        + (img ? img.outerHTML : '<span style="font-size:5rem;opacity:0.2">📦</span>')
        + '</div>'
        + '<div class="cf-qv-info">'
        + (vendor ? '<p class="cf-qv-vendor">' + vendor.textContent.trim() + '</p>' : '')
        + (title  ? '<h3 class="cf-qv-title">'  + title.textContent.trim()  + '</h3>' : '')
        + (sku    ? '<p style="font-size:0.72rem;color:var(--text-tertiary)">' + sku.textContent.trim() + '</p>' : '')
        + (price  ? '<p class="cf-qv-price">'  + price.textContent.trim()  + '</p>' : '')
        + (specs  ? '<p class="cf-qv-specs">'  + specs.textContent.trim().substring(0, 200) + '</p>' : '')
        + '<div class="cf-qv-actions">'
        + '<a href="' + prodUrl + '" class="cf-btn cf-btn--primary">View Full Details</a>'
        + '</div>'
        + '</div></div>';
    })
    .catch(function () {
      content.innerHTML = '<p style="padding:2rem;text-align:center;color:var(--text-secondary)">Unable to load product preview.</p>';
    });
  };

  window.cfCloseQV = function () {
    var overlay = document.getElementById('cf-qv-overlay');
    var modal   = document.getElementById('cf-qv-modal');
    overlay && overlay.classList.remove('is-open');
    modal   && modal.classList.remove('is-open');
    document.body.style.overflow = '';
  };

  // Close on Escape key
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') {
      cfCloseQV();
      cfCloseDrawer();
      cfCloseCompare();
    }
  });

  /* ─────────────────────────────────────────────────────────
     COMPARE
  ───────────────────────────────────────────────────────── */
  window.cfCompareToggle = function (cb) {
    var card  = cb.closest('.cf-product-card');
    var id    = cb.value;
    var title = card ? (card.dataset.compareTitle || 'Product') : 'Product';
    var price = card ? (card.dataset.comparePrice || '') : '';
    var img   = card ? (card.dataset.compareImg   || '') : '';

    if (cb.checked) {
      if (cfState.compareIds.length >= 4) {
        cb.checked = false;
        alert('You can compare up to 4 products at once.');
        return;
      }
      cfState.compareIds.push(id);
      cfState.compareItems.push({ id: id, title: title, price: price, img: img });
    } else {
      cfState.compareIds   = cfState.compareIds.filter(function (i) { return i !== id; });
      cfState.compareItems = cfState.compareItems.filter(function (i) { return i.id !== id; });
    }
    cfRenderCompareBar();
  };

  function cfRenderCompareBar () {
    var bar   = document.getElementById('cf-compare-bar');
    var count = document.getElementById('cf-compare-count');
    var items = document.getElementById('cf-compare-items');
    if (!bar) return;
    if (cfState.compareIds.length === 0) {
      bar.style.display = 'none';
      return;
    }
    bar.style.display = 'flex';
    if (count) count.textContent = cfState.compareIds.length;
    if (items) {
      items.innerHTML = cfState.compareItems.map(function (item) {
        return '<div class="cf-compare-thumb">'
          + (item.img ? '<img src="' + item.img + '" alt="' + cfEsc(item.title) + '">' : '📦')
          + '</div>';
      }).join('');
    }
  }

  window.cfClearCompare = function () {
    cfState.compareIds  = [];
    cfState.compareItems = [];
    document.querySelectorAll('.cf-compare-input').forEach(function (cb) { cb.checked = false; });
    cfRenderCompareBar();
  };

  window.cfOpenCompare = function () {
    if (cfState.compareItems.length < 2) {
      alert('Please select at least 2 products to compare.');
      return;
    }
    var modal   = document.getElementById('cf-compare-modal');
    var overlay = document.getElementById('cf-compare-overlay');
    var grid    = document.getElementById('cf-compare-grid');
    if (!modal) return;
    if (grid) {
      grid.innerHTML = cfState.compareItems.map(function (item) {
        return '<div class="cf-compare-card">'
          + '<img src="' + (item.img || '') + '" alt="' + cfEsc(item.title) + '" onerror="this.style.display=\'none\'">'
          + '<h4>' + cfEsc(item.title) + '</h4>'
          + '<p class="cf-card-price">' + (item.price || 'Price on Request') + '</p>'
          + '</div>';
      }).join('');
    }
    modal.style.display = 'flex';
    overlay && (overlay.style.display = 'block');
    document.body.style.overflow = 'hidden';
  };

  window.cfCloseCompare = function () {
    var modal   = document.getElementById('cf-compare-modal');
    var overlay = document.getElementById('cf-compare-overlay');
    if (modal)   modal.style.display   = 'none';
    if (overlay) overlay.style.display = 'none';
    document.body.style.overflow = '';
  };

  /* ─────────────────────────────────────────────────────────
     LOAD MORE
  ───────────────────────────────────────────────────────── */
  window.cfLoadMore = function (btn) {
    var nextUrl = btn.dataset.nextUrl;
    if (!nextUrl) return;
    btn.classList.add('is-loading');
    btn.textContent = 'Loading…';

    fetch(nextUrl + '&sections=collection-filter', {
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    })
    .then(function (res) { return res.json(); })
    .then(function (data) {
      var html   = data['collection-filter'];
      if (!html) return;
      var parser = new DOMParser();
      var doc    = parser.parseFromString(html, 'text/html');
      var newCards = doc.querySelectorAll('.cf-product-card');
      var grid     = document.querySelector('[data-product-grid]');
      if (grid) {
        newCards.forEach(function (card) { grid.appendChild(card.cloneNode(true)); });
      }
      // Update Load More button
      var newBtn = doc.getElementById('cf-load-more');
      if (newBtn) {
        btn.dataset.nextUrl  = newBtn.dataset.nextUrl;
        btn.classList.remove('is-loading');
        btn.innerHTML = 'Load More Products <span class="cf-load-more-count">'
          + newBtn.querySelector('.cf-load-more-count').textContent + '</span>';
      } else {
        btn.closest('.cf-pagination').innerHTML = '';
      }
    })
    .catch(function () {
      btn.classList.remove('is-loading');
      btn.textContent = 'Load More Products';
    });
  };

  /* ─────────────────────────────────────────────────────────
     HELPERS
  ───────────────────────────────────────────────────────── */
  function cfFmtNum (n) {
    return n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function cfEsc (str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

}());
